import React from "react";
import { Card, CardContent, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import CardHeader from "./CardHeader.jsx";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import * as f from "../../../utils/formatter.js";
import { insurance } from "shared/src/client.mjs";

/**
 * @typedef {Object} Client
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} email
 */

/**
 * @typedef {Object} ClientInfoCardProps
 * @property {Client} client
 * @property {boolean} displayMinimalInfo
 */

/**
 * @param {ClientInfoCardProps} props
 */
const ClientInfoCard = ({ client, displayMinimalInfo = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const roles = useRoles();
  const canSeeBirthInfo = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  const canSeeClientInsuranceCompany = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );
  return (
    <Card>
      <CardContent>
        <CardHeader
          title={t("client.info.card.title")}
          color={theme.palette.info.main}
          faIcon={faUser}
        />
        <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
          <Typography variant="body1">
            <strong>ID klienta</strong> <br />
            {f.value(client.labs_id)}
          </Typography>
          <Typography variant="body1">
            <strong>{t("client.info.fullName")}</strong> <br />
            {f.fullName(client)}
          </Typography>
          {!displayMinimalInfo && (
            <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
              {canSeeBirthInfo && (
                <>
                  <Typography variant="body1">
                    <strong>{t("client.info.birthNumber")}</strong> <br />
                    {client.birth_number}
                  </Typography>
                  <Typography variant="body1">
                    <strong>{t("client.info.birthDate")}</strong>
                    <br />
                    {f.date(client.birthdate)}
                  </Typography>
                </>
              )}
              <Typography variant="body1">
                <strong>{t("client.info.age")}</strong>
                <br />
                {f.ageFromBirthDate(client.birthdate)}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
          {canSeeClientInsuranceCompany && (
            <Typography variant="body1">
              <strong>Pojišťovna</strong> <br />
              {f.value(insurance[client.insurance_code])}
            </Typography>
          )}
          <Typography variant="body1">
            <strong>{t("entity.client.sex")}</strong> <br />
            {client.sex != null ? t(`sex.${client.sex}`) : f.value(null)}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ClientInfoCard;
